import { CurveData } from '~/domain/patient/dvh';
import { relativeDosePercent } from '~/utils/dose/doseDisplayValidation';
import ChartTooltip from './ChartTooltip';
import { ChartPalette, LinearScale } from './chartStyle';
import DvhTooltipContent, { cappedDoseGy } from './DvhTooltipContent';
import HoverSurface from './HoverSurface';
import { HoverCurve, nearestPointAcrossCurves } from './nearestPoint';
import { PlotArea } from './plotArea';

/** The volume below which the tooltip hangs above its point, not below. */
const FLIP_UP_BELOW_VOLUME_PERCENT = 40;

/** The relative dose below which the tooltip extends to the right of its point. */
const LEAN_RIGHT_BELOW_RELATIVE_PERCENT = 50;

/** The tooltip's text size, in pixels, on the compact and the expanded chart alike. */
const TOOLTIP_FONT_SIZE = 8;

/** The per-curve lookup arrays a hover reads, built once per data change. */
export function hoverCurvesOf(curves: CurveData[]): HoverCurve[] {
    return curves.map((curve) => ({
        xs: curve.data.map((point) => point.x),
        ys: curve.data.map((point) => point.y),
    }));
}

/**
 * The crosshair and the tooltip over the DVH plot. Each move snaps to the
 * nearest plotted point across every curve and draws the crosshair through
 * it; the curves beneath do not re-render on a move. The tooltip leans by
 * the point's relative dose, or, when no relative dose exists, away from
 * the nearer side of the plot.
 */
export default function DvhHoverLayer({
    curves,
    hoverCurves,
    xScale,
    yScale,
    plot,
    normalizationValueGy,
    palette,
}: {
    curves: CurveData[];
    hoverCurves: HoverCurve[];
    xScale: LinearScale;
    yScale: LinearScale;
    plot: PlotArea;
    normalizationValueGy: number;
    palette: ChartPalette;
}) {
    return (
        <HoverSurface plot={plot} testId="dvh-hover-target">
            {(cursor) => {
                const nearest = cursor
                    ? nearestPointAcrossCurves(
                          hoverCurves,
                          {
                              x: xScale.invert(cursor.x - plot.left),
                              y: yScale.invert(cursor.y - plot.top),
                          },
                          xScale,
                          yScale
                      )
                    : undefined;
                const curve = nearest ? curves[nearest.curveIndex] : undefined;
                if (!nearest || !curve) {
                    return null;
                }
                const point = {
                    x: plot.left + xScale(nearest.x),
                    y: plot.top + yScale(nearest.y),
                };
                const relativePercent = relativeDosePercent(
                    cappedDoseGy(nearest.x, curve),
                    normalizationValueGy
                );
                const leansRight =
                    relativePercent === undefined
                        ? point.x - plot.left < plot.width / 2
                        : relativePercent < LEAN_RIGHT_BELOW_RELATIVE_PERCENT;
                return (
                    <>
                        <g data-testid="dvh-crosshair" pointerEvents="none">
                            <line
                                x1={point.x}
                                x2={point.x}
                                y1={plot.top}
                                y2={plot.top + plot.height}
                                stroke={palette.crosshair}
                                strokeWidth={1}
                                strokeDasharray="6 6"
                            />
                            <line
                                x1={plot.left}
                                x2={plot.left + plot.width}
                                y1={point.y}
                                y2={point.y}
                                stroke={palette.crosshair}
                                strokeWidth={1}
                                strokeDasharray="6 6"
                            />
                            <circle
                                cx={point.x}
                                cy={point.y}
                                r={3}
                                fill={curve.color}
                                stroke={palette.crosshair}
                            />
                        </g>
                        <ChartTooltip
                            data-testid="dvh-tooltip"
                            plot={plot}
                            palette={palette}
                            anchor={point}
                            lean={leansRight ? 'right' : 'left'}
                            flipUp={nearest.y < FLIP_UP_BELOW_VOLUME_PERCENT}
                            style={{
                                padding: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                fontSize: TOOLTIP_FONT_SIZE,
                                minWidth: 360,
                            }}
                        >
                            <DvhTooltipContent
                                curve={curve}
                                doseGy={nearest.x}
                                volumePercent={nearest.y}
                                normalizationValueGy={normalizationValueGy}
                            />
                        </ChartTooltip>
                    </>
                );
            }}
        </HoverSurface>
    );
}
